/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  ViewChild,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ElementRef,
  Optional,
  AfterViewChecked,
  ViewEncapsulation,
} from '@angular/core';
import {
  trigger,
  state,
  style,
  animate,
  transition,
  AnimationEvent,
} from '@angular/animations';
import {TemplatePortal, PortalHostDirective, Dir, LayoutDirection} from '../core';
import'rxjs/add/operator/map';

/**
 * These position states are used internally as animation states for the tab body. Setting the
 * position state to left, right, or center will transition the tab body from its current
 * position to its respective state. If there is not current position (void, in the case of a new
 * tab body), then there will be no transition animation to its state.
 *
 * In the case of a new tab body that should immediately be centered with an animating transition,
 * then left-origin-center or right-origin-center can be used, which will use left or right as its
 * psuedo-prior state.
 */
export type MdTabBodyPositionState =
    'left' | 'center' | 'right' | 'left-origin-center' | 'right-origin-center';

/**
 * The origin state is an internally used state that is set on a new tab body indicating if it
 * began to the left or right of the prior selected index. For example, if the selected index was
 * set to 1, and a new tab is created and selected at index 2, then the tab body would have an
 * origin of right because its index was greater than the prior selected index.
 */
export type MdTabBodyOriginState = 'left' | 'right';
/**
 * Wrapper for the contents of a tab.
 * \@docs-private
 */
export class MdTabBody implements OnInit, AfterViewChecked {
/**
 * The portal host inside of this container into which the tab body content will be loaded.
 */
_portalHost: PortalHostDirective;
/**
 * Event emitted when the tab begins to animate towards the center as the active tab.
 */
onCentering: EventEmitter<number> = new EventEmitter<number>();
/**
 * Event emitted when the tab completes its animation towards the center.
 */
onCentered: EventEmitter<void> = new EventEmitter<void>(true);
/**
 * The tab body content to display.
 */
_content: TemplatePortal;
/**
 * The shifted index position of the tab body, where zero represents the active center tab.
 */
_position: MdTabBodyPositionState;
/**
 * @param {?} position
 * @return {?}
 */
set position(position: number) {
    if (position < 0) {
      this._position = this._getLayoutDirection() == 'ltr' ? 'left' : 'right';
    } else if (position > 0) {
      this._position = this._getLayoutDirection() == 'ltr' ? 'right' : 'left';
    } else {
      this._position = 'center';
    }
  }
/**
 * The origin position from which this tab should appear when it is centered into view.
 */
_origin: MdTabBodyOriginState;
/**
 * The origin position from which this tab should appear when it is centered into view.
 * @param {?} origin
 * @return {?}
 */
set origin(origin: number) {
    if (origin == null) { return; }

    const /** @type {?} */ dir = this._getLayoutDirection();
    if ((dir == 'ltr' && origin <= 0) || (dir == 'rtl' && origin > 0)) {
      this._origin = 'left';
    } else {
      this._origin = 'right';
    }
  }
/**
 * @param {?} _dir
 * @param {?} _elementRef
 */
constructor(
private _dir: Dir,
private _elementRef: ElementRef) { }
/**
 * After initialized, check if the content is centered and has an origin. If so, set the
 * special position states that transition the tab from the left or right before centering.
 * @return {?}
 */
ngOnInit() {
    if (this._position == 'center' && this._origin) {
      this._position = this._origin == 'left' ? 'left-origin-center' : 'right-origin-center';
    }
  }
/**
 * After the view has been set, check if the tab content is set to the center and attach the
 * content if it is not already attached.
 * @return {?}
 */
ngAfterViewChecked() {
    if (this._isCenterPosition(this._position) && !this._portalHost.hasAttached()) {
      this._portalHost.attach(this._content);
    }
  }
/**
 * @param {?} e
 * @return {?}
 */
_onTranslateTabStarted(e: AnimationEvent) {
    if (this._isCenterPosition(e.toState)) {
      this.onCentering.emit(this._elementRef.nativeElement.clientHeight);
    }
  }
/**
 * @param {?} e
 * @return {?}
 */
_onTranslateTabComplete(e: AnimationEvent) {
    // If the end state is that the tab is not centered, then detach the content.
    if (!this._isCenterPosition(e.toState) && !this._isCenterPosition(this._position)) {
      this._portalHost.detach();
    }

    // If the transition to the center is complete, emit an event.
    if (this._isCenterPosition(e.toState) && this._isCenterPosition(this._position)) {
      this.onCentered.emit();
    }
  }
/**
 * The text direction of the containing app.
 * @return {?}
 */
_getLayoutDirection(): LayoutDirection {
    return this._dir && this._dir.value === 'rtl' ? 'rtl' : 'ltr';
  }
/**
 * Whether the provided position state is considered center, regardless of origin.
 * @param {?} position
 * @return {?}
 */
private _isCenterPosition(position: MdTabBodyPositionState|string): boolean {
    return position == 'center' ||
        position == 'left-origin-center' ||
        position == 'right-origin-center';
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-tab-body, mat-tab-body',
  templateUrl: 'tab-body.html',
  styleUrls: ['tab-body.css'],
  encapsulation: ViewEncapsulation.None,
  host: {
    'class': 'mat-tab-body',
  },
  animations: [
    trigger('translateTab', [
      state('void', style({transform: 'translate3d(0%, 0, 0)'})),
      state('left', style({transform: 'translate3d(-100%, 0, 0)'})),
      state('left-origin-center', style({transform: 'translate3d(0%, 0, 0)'})),
      state('right-origin-center', style({transform: 'translate3d(0%, 0, 0)'})),
      state('center', style({transform: 'translate3d(0%, 0, 0)'})),
      state('right', style({transform: 'translate3d(100%, 0, 0)'})),
      transition('* => left, * => right, left => center, right => center',
          animate('500ms cubic-bezier(0.35, 0, 0.25, 1)')),
      transition('void => left-origin-center', [
        style({transform: 'translate3d(-100%, 0, 0)'}),
        animate('500ms cubic-bezier(0.35, 0, 0.25, 1)')
      ]),
      transition('void => right-origin-center', [
        style({transform: 'translate3d(100%, 0, 0)'}),
        animate('500ms cubic-bezier(0.35, 0, 0.25, 1)')
      ])
    ])
  ]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Dir, decorators: [{ type: Optional }, ]},
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_portalHost': [{ type: ViewChild, args: [PortalHostDirective, ] },],
'onCentering': [{ type: Output },],
'onCentered': [{ type: Output },],
'_content': [{ type: Input, args: ['content', ] },],
'position': [{ type: Input, args: ['position', ] },],
'origin': [{ type: Input, args: ['origin', ] },],
};
}

function MdTabBody_tsickle_Closure_declarations() {
/** @type {?} */
MdTabBody.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdTabBody.ctorParameters;
/** @type {?} */
MdTabBody.propDecorators;
/**
 * The portal host inside of this container into which the tab body content will be loaded.
 * @type {?}
 */
MdTabBody.prototype._portalHost;
/**
 * Event emitted when the tab begins to animate towards the center as the active tab.
 * @type {?}
 */
MdTabBody.prototype.onCentering;
/**
 * Event emitted when the tab completes its animation towards the center.
 * @type {?}
 */
MdTabBody.prototype.onCentered;
/**
 * The tab body content to display.
 * @type {?}
 */
MdTabBody.prototype._content;
/**
 * The shifted index position of the tab body, where zero represents the active center tab.
 * @type {?}
 */
MdTabBody.prototype._position;
/**
 * The origin position from which this tab should appear when it is centered into view.
 * @type {?}
 */
MdTabBody.prototype._origin;
/** @type {?} */
MdTabBody.prototype._dir;
/** @type {?} */
MdTabBody.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
